import { Component, OnInit, inject, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { GrowthIntelligenceService } from './growth-intelligence.service';

interface RevOpsPayload {
  attribution?: { id?: string; totalAttributedRevenue?: number; revenueSourceMode?: string } | null;
  funnel?: { id?: string; bottleneckSummary?: string } | null;
  forecast?: { id?: string; forecastType?: string; horizonDays?: number } | null;
  contentLeaderboard?: Array<{ contentAssetId?: string; score?: number; leadCount?: number }>;
}

@Component({
  standalone: true,
  selector: 'ain-growth-intelligence-dashboard',
  imports: [AinPageComponent, AinStateComponent, InfoHintComponent, CardModule, TableModule, ButtonModule, AinDatePipe],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.intelligence.dashboard.title')"
      [description]="t('growth.intelligence.dashboard.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.intelligence.dashboard.help.title'"
        [descriptionKey]="'growth.intelligence.dashboard.help.description'"
        [bulletKeys]="[
          'growth.intelligence.dashboard.help.bullets.advisory',
          'growth.intelligence.dashboard.help.bullets.ufc',
          'growth.intelligence.dashboard.help.bullets.sales'
        ]" />

      <div class="toolbar">
        <button pButton type="button" severity="secondary" icon="pi pi-refresh" [label]="t('common.refresh')" (click)="load()"></button>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('growth.intelligence.dashboard.errorLoad')" [message]="error() ?? ''" />
      } @else {
        <section class="cards">
          <p-card [header]="t('growth.intelligence.dashboard.cards.attribution')">
            @if (payload()?.attribution) {
              <p><strong>{{ t('growth.intelligence.dashboard.fields.revenue') }}:</strong> {{ payload()!.attribution!.totalAttributedRevenue ?? 0 }}</p>
              <p><strong>{{ t('growth.intelligence.dashboard.fields.mode') }}:</strong> {{ payload()!.attribution!.revenueSourceMode ?? '-' }}</p>
            } @else {
              <p>{{ t('growth.intelligence.dashboard.emptySection') }}</p>
            }
          </p-card>
          <p-card [header]="t('growth.intelligence.dashboard.cards.funnelDropOff')">
            @if (payload()?.funnel) {
              <p>{{ payload()!.funnel!.bottleneckSummary ?? t('growth.intelligence.dashboard.emptySection') }}</p>
            } @else {
              <p>{{ t('growth.intelligence.dashboard.emptySection') }}</p>
            }
          </p-card>
          <p-card [header]="t('growth.intelligence.dashboard.cards.cohortRetention')">
            <p>{{ t('growth.intelligence.dashboard.cohortPlaceholder') }}</p>
          </p-card>
          <p-card [header]="t('growth.intelligence.dashboard.cards.clvChurn')">
            <p>{{ t('growth.intelligence.dashboard.clvChurnAdvisory') }}</p>
          </p-card>
          <p-card [header]="t('growth.intelligence.dashboard.cards.forecast')">
            @if (payload()?.forecast) {
              <p><strong>{{ t('growth.intelligence.dashboard.fields.type') }}:</strong> {{ payload()!.forecast!.forecastType ?? '-' }}</p>
              <p><strong>{{ t('growth.intelligence.dashboard.fields.horizon') }}:</strong> {{ payload()!.forecast!.horizonDays ?? '-' }}</p>
            } @else {
              <p>{{ t('growth.intelligence.dashboard.emptySection') }}</p>
            }
          </p-card>
        </section>

        <section class="card">
          <div class="section-title"><h3>{{ t('growth.intelligence.dashboard.cards.contentLeaderboard') }}</h3></div>
          @if (leaderboard().length === 0) {
            <ain-state type="empty" [title]="t('growth.intelligence.dashboard.emptySection')" />
          } @else {
            <p-table [value]="leaderboard()" [rows]="5" [paginator]="true">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('growth.intelligence.dashboard.columns.contentAsset') }}</th>
                  <th>{{ t('growth.intelligence.dashboard.columns.score') }}</th>
                  <th>{{ t('growth.intelligence.dashboard.columns.leads') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.contentAssetId ?? '-' }}</td>
                  <td>{{ row.score ?? 0 }}</td>
                  <td>{{ row.leadCount ?? 0 }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
          @if (refreshedAt()) {
            <p class="meta">{{ t('growth.intelligence.dashboard.refreshedAt') }}: {{ refreshedAt() | ainDate:'datetime' }}</p>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
    .cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 12px; margin-bottom: 12px; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; display: grid; gap: 10px; }
    .section-title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .meta { color: #6b7280; font-size: 0.875rem; margin: 0; }
  `]
})
export class GrowthIntelligenceDashboardComponent implements OnInit {
  private readonly service = inject(GrowthIntelligenceService);
  private readonly i18n = inject(LocalizationService);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly payload = signal<RevOpsPayload | null>(null);
  readonly leaderboard = signal<Array<{ contentAssetId?: string; score?: number; leadCount?: number }>>([]);
  readonly refreshedAt = signal<string | null>(null);

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.service.getReadModel('revops-dashboard').subscribe({
      next: snapshot => {
        try {
          const parsed = JSON.parse(snapshot.payloadJson) as RevOpsPayload;
          this.payload.set(parsed);
          this.leaderboard.set(parsed.contentLeaderboard ?? []);
        } catch {
          this.payload.set(null);
          this.leaderboard.set([]);
        }
        this.refreshedAt.set(snapshot.refreshedAtUtc);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('growth.intelligence.dashboard.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  t(key: string): string { return this.i18n.translate(key); }
}
